<script setup>
import {computed, ref} from "vue";
import axios from "axios";
import {axiosCatchHandler} from "../../modules/sharedGlobal";

const props = defineProps({
  recoveryCodes: {
    required: false,
    type: Object,
    default: () => ({}),
  }
});

const loading = ref(false);
const code = ref(null);
const errors = ref({});

const recoveryCodes = ref(props.recoveryCodes);
const qrCode = ref(null);
const secretKey = ref(null);

const emit = defineEmits(['update:recoveryCodes', 'setupConfirmed']);

const twoFactorAuthSetupPending = computed(() => {
  return qrCode.value !== null;
});

const formattedSecretKey = computed(() => {
  if (secretKey.value === null) {
    return null;
  }
  // add a space after each group of 4
  return secretKey.value.replace(/(.{4})(?!$)/g, "$1 ");
});

const handleSetup = async () => {
  loading.value = true;

  try {
    const {data} = await axios.get('/two-factor/setup')

    qrCode.value = data.qrCode;
    secretKey.value = data.secretKey;
    loading.value = false;
  } catch (error) {
    loading.value = false;
  }
}

const handleConfirm = async () => {
  loading.value = true;

  try {
    const {data} = await axios.post('/two-factor/confirm', {
      code: code.value,
    });
    recoveryCodes.value = data.recoveryCodes;

    emit('update:recoveryCodes', recoveryCodes.value);
    emit('setupConfirmed');

    loading.value = false;
  } catch (error) {
    errors.value = error.response.data.errors;
    loading.value = false;
    axiosCatchHandler(error, () => {});
  }
}
</script>

<template>
  <div
    class="d-flex justify-content-center p-2"
  >
    <div class="text-center two-factor-container mw-100">
      <div
        class="alert-danger text-danger border-radius-6 py-2 d-flex justify-content-center align-items-center flex-wrap"
      >
        <svg-vue
          class="svg mr-0 mr-lg-2"
          icon="icons/warning-bright-red"
        />
        <span class="font-weight-bold mx-2 mx-lg-0">
          Two-factor authentication disabled
        </span>
      </div>

      <div v-if="!twoFactorAuthSetupPending">
        <p class="font-weight-bold my-4">
          Click below to generate a QR code and set up Two-Factor Authentication:
        </p>
        <button
          :disabled="loading"
          class="btn btn-lg button font-weight-bold btn-info m-0 mb-2 font-weight-bold"
          @click="handleSetup"
        >
          Setup Two-Factor Authentication
        </button>
      </div>
      <div v-else>
        <p class="font-weight-bold mb-1 mt-4">
          Scan the QR code to enable two-factor authentication:
        </p>
        <p>
          You will need Google Authenticator or similar to complete this process.
        </p>

        <div
          v-html="qrCode"
        />

        <p class="font-weight-bold text-light-grey">
          Secret key: <span class="text-uppercase text-info">{{ formattedSecretKey }}</span>
        </p>

        <div class="my-4">
          <input
            v-model="code"
            type="text"
            required
            class="form-control m-0 m-auto w-75"
            :class="{
              'error': errors.code
            }"
            placeholder="Enter Authentication Code"
            @keyup.enter="handleConfirm"
          >
          <div
            v-if="errors.code"
            class="text-danger"
          >
            <small
              v-for="(error, index) in errors.code"
              :key="index"
              v-html="`${error}<br/>`"
            />
          </div>
        </div>

        <button
          :disabled="loading"
          class="btn btn-lg button font-weight-bold btn-info m-0 mb-2 font-weight-bold"
          @click="handleConfirm"
        >
          Submit
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.two-factor-container {
  width: 40rem;
}

.text-danger {
  color: #F34F4F !important;
}

.alert-danger {
  background: #FFEEEA;
}

.svg {
  max-width: 32px !important;
}

.text-info {
  color: #00c6dd;
}
</style>
